import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { API_BASE, useAuth } from '../lib/auth';
import { AdminPageShell } from './AdminActivity';

type FormStatus = 'draft' | 'live' | 'closed' | 'archived';
type EmailPolicy = 'off' | 'optional' | 'required_verified';
type FieldType = 'short_text' | 'long_text' | 'single_select' | 'multi_select';

type FormItem = {
  id: string;
  slug: string | null;
  title: string;
  description: string | null;
  status: FormStatus;
  is_default: boolean;
  email_policy: EmailPolicy;
  voicenote_enabled: boolean;
  is_locked: boolean;
  updated_at: string;
  submission_count: number;
};

type BuilderField = {
  id: string;
  type: FieldType;
  label: string;
  placeholder: string;
  help_text: string;
  is_required: boolean;
  options: string[];
};

const blankField = (): BuilderField => ({
  id: crypto.randomUUID(),
  type: 'long_text',
  label: '',
  placeholder: '',
  help_text: '',
  is_required: false,
  options: [],
});

export default function AdminForms() {
  const params = useParams();
  const mode = params.id ? 'edit' : location.pathname.endsWith('/new') ? 'new' : 'list';

  if (mode === 'list') return <FormsList />;
  return <FormBuilder formId={params.id ?? null} />;
}

function FormsList() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [forms, setForms] = useState<FormItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function loadForms() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/forms`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { ok: boolean; forms: FormItem[] };
      setForms(data.forms ?? []);
    } catch (err) {
      console.error('forms_fetch_failed', err);
      setError("Couldn't load forms.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadForms();
  }, []);

  async function signOut() {
    await logout();
    navigate('/login', { replace: true });
  }

  async function archiveForm(form: FormItem) {
    const typed = window.prompt(
      `Archive "${form.title}"? Its public link will stop accepting submissions.\n\nType the form title to confirm.`
    );
    if (typed !== form.title) return;
    await fetch(`${API_BASE}/api/admin/forms/${form.id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    loadForms();
  }

  return (
    <AdminPageShell active="Forms" onSignOut={signOut}>
      <main className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-5 sm:py-8">
        <div className="flex items-center justify-between gap-4 mb-5">
          <div>
            <h1 className="font-bagel text-[30px] text-burgundy leading-none">Forms</h1>
            <p className="text-[13px] text-burgundy-faint mt-1">
              Build custom links for listener callouts, episode segments, and quick asks.
            </p>
          </div>
          <Link
            to="/admin/forms/new"
            className="min-h-11 px-4 rounded-lg bg-pink text-cream-bright font-bold text-[13px] inline-flex items-center"
          >
            New form
          </Link>
        </div>

        {error && <div className="mb-4 text-danger text-[13px] font-semibold">{error}</div>}

        <div className="bg-cream-bright border border-burgundy/[0.12] rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-6 text-burgundy-faint text-sm">Loading...</div>
          ) : forms.length === 0 ? (
            <div className="p-6 text-burgundy-faint text-sm">No forms yet.</div>
          ) : (
            forms.map((form) => (
              <div
                key={form.id}
                className="p-4 border-b last:border-b-0 border-burgundy/[0.10] flex flex-col md:flex-row md:items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-burgundy">{form.title}</div>
                    <Badge>{form.status}</Badge>
                    {form.is_default && <Badge>default</Badge>}
                    {form.is_locked && <Badge>locked</Badge>}
                  </div>
                  <div className="text-[13px] text-burgundy-faint mt-1">
                    {form.slug ? `/f/${form.slug}` : '/'} · {form.submission_count} submissions
                  </div>
                </div>
                {form.slug && (
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(`${location.origin}/f/${form.slug}`)}
                    className="min-h-10 px-3 rounded-lg border border-burgundy/[0.14] text-burgundy-soft text-[12px] font-bold"
                  >
                    Copy link
                  </button>
                )}
                <Link
                  to={`/admin/forms/${form.id}`}
                  className="min-h-10 px-3 rounded-lg border border-burgundy/[0.14] text-burgundy-soft text-[12px] font-bold inline-flex items-center justify-center"
                >
                  Dashboard
                </Link>
                <Link
                  to={`/admin/forms/${form.id}/edit`}
                  className="min-h-10 px-3 rounded-lg bg-burgundy text-cream-bright text-[12px] font-bold inline-flex items-center justify-center"
                >
                  Edit
                </Link>
                {!form.is_default && (
                  <button
                    type="button"
                    onClick={() => archiveForm(form)}
                    className="min-h-10 px-3 rounded-lg border border-danger/30 text-danger text-[12px] font-bold"
                  >
                    Archive
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </main>
    </AdminPageShell>
  );
}

function FormBuilder({ formId }: { formId: string | null }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState<FormStatus>('draft');
  const [emailPolicy, setEmailPolicy] = useState<EmailPolicy>('optional');
  const [voicenoteEnabled, setVoicenoteEnabled] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [fields, setFields] = useState<BuilderField[]>([blankField()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!formId) return;
    fetch(`${API_BASE}/api/admin/forms/${formId}`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as { ok: boolean; form: FormItem; fields: BuilderField[] };
      })
      .then((data) => {
        setTitle(data.form.title);
        setDescription(data.form.description ?? '');
        setSlug(data.form.slug ?? '');
        setStatus(data.form.status);
        setEmailPolicy(data.form.email_policy);
        setVoicenoteEnabled(data.form.voicenote_enabled);
        setIsDefault(data.form.is_default);
        setIsLocked(data.form.is_locked);
        setFields(
          data.fields.map((f) => ({
            ...f,
            placeholder: f.placeholder ?? '',
            help_text: f.help_text ?? '',
            options: Array.isArray(f.options) ? f.options : [],
          }))
        );
      })
      .catch((err) => {
        console.error('form_load_failed', err);
        setError("Couldn't load this form.");
      });
  }, [formId]);

  const publicLink = useMemo(() => (slug ? `${location.origin}/f/${slug}` : ''), [slug]);

  async function signOut() {
    await logout();
    navigate('/login', { replace: true });
  }

  function updateField(id: string, patch: Partial<BuilderField>) {
    setFields((list) => list.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }

  function moveField(index: number, dir: -1 | 1) {
    setFields((list) => {
      const next = [...list];
      const target = index + dir;
      if (target < 0 || target >= next.length) return list;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body = {
        title,
        description,
        slug,
        status,
        email_policy: emailPolicy,
        voicenote_enabled: voicenoteEnabled,
        fields,
      };
      const res = await fetch(`${API_BASE}/api/admin/forms${formId ? `/${formId}` : ''}`, {
        method: formId ? 'PATCH' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      navigate('/admin/forms');
    } catch (err) {
      console.error('form_save_failed', err);
      setError("Couldn't save this form. Check the slug and required fields.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell active="Forms" onSignOut={signOut}>
      <main className="max-w-6xl mx-auto w-full px-4 sm:px-6 py-5 sm:py-8">
        <form onSubmit={save} className="grid lg:grid-cols-[1fr_360px] gap-5">
          <section className="bg-cream-bright border border-burgundy/[0.12] rounded-xl p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h1 className="font-bagel text-[30px] text-burgundy leading-none">
                  {formId ? 'Edit form' : 'New form'}
                </h1>
                {isLocked && (
                  <p className="text-[12px] text-orange mt-1 font-semibold">
                    Field editing is locked because this form has submissions.
                  </p>
                )}
              </div>
              <Link to="/admin/forms" className="text-[13px] text-pink-deep font-bold">
                Back
              </Link>
            </div>

            {error && <div className="mb-4 text-danger text-[13px] font-semibold">{error}</div>}

            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <Field label="Title" value={title} onChange={setTitle} required />
              <Field
                label="Slug"
                value={slug}
                onChange={setSlug}
                disabled={isDefault}
                placeholder="listener-check-in"
                required
              />
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-[11px] font-bold uppercase text-burgundy-faint">Description</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="rounded-lg border border-burgundy/[0.18] bg-cream px-3 py-2 text-[14px] focus:outline-none focus:border-pink"
                />
              </label>
              <Select label="Status" value={status} onChange={(v) => setStatus(v as FormStatus)} disabled={isDefault}>
                <option value="draft">Draft</option>
                <option value="live">Live</option>
                <option value="closed">Closed</option>
              </Select>
              <Select label="Email policy" value={emailPolicy} onChange={(v) => setEmailPolicy(v as EmailPolicy)}>
                <option value="off">Off</option>
                <option value="optional">Optional</option>
                <option value="required_verified">Required</option>
              </Select>
            </div>

            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bagel text-[22px] text-burgundy">Fields</h2>
              {!isLocked && (
                <button
                  type="button"
                  onClick={() => setFields((list) => [...list, blankField()])}
                  className="min-h-10 px-3 rounded-lg bg-burgundy text-cream-bright text-[12px] font-bold"
                >
                  Add field
                </button>
              )}
            </div>

            <div className="space-y-3">
              {fields.map((field, index) => (
                <div key={field.id} className="rounded-xl border border-burgundy/[0.12] bg-cream-soft p-3">
                  <div className="grid sm:grid-cols-[150px_1fr] gap-2 mb-2">
                    <Select
                      label="Type"
                      value={field.type}
                      disabled={isLocked}
                      onChange={(v) => updateField(field.id, { type: v as FieldType })}
                    >
                      <option value="short_text">Short text</option>
                      <option value="long_text">Long text</option>
                      <option value="single_select">Single select</option>
                      <option value="multi_select">Multi select</option>
                    </Select>
                    <Field
                      label="Label"
                      value={field.label}
                      disabled={isLocked}
                      onChange={(v) => updateField(field.id, { label: v })}
                      required
                    />
                    <Field
                      label="Placeholder"
                      value={field.placeholder}
                      disabled={isLocked}
                      onChange={(v) => updateField(field.id, { placeholder: v })}
                    />
                    <Field
                      label="Help text"
                      value={field.help_text}
                      disabled={isLocked}
                      onChange={(v) => updateField(field.id, { help_text: v })}
                    />
                  </div>
                  {(field.type === 'single_select' || field.type === 'multi_select') && (
                    <Field
                      label="Options, comma separated"
                      value={field.options.join(', ')}
                      disabled={isLocked}
                      onChange={(v) =>
                        updateField(field.id, {
                          options: v.split(',').map((o) => o.trim()).filter(Boolean),
                        })
                      }
                      required
                    />
                  )}
                  <div className="flex gap-2 items-center mt-3 flex-wrap">
                    <label className="inline-flex items-center gap-2 text-[12px] font-bold text-burgundy-soft">
                      <input
                        type="checkbox"
                        checked={field.is_required}
                        disabled={isLocked}
                        onChange={(e) => updateField(field.id, { is_required: e.target.checked })}
                      />
                      Required
                    </label>
                    {!isLocked && (
                      <>
                        <button type="button" onClick={() => moveField(index, -1)} className="text-[12px] font-bold text-burgundy-faint">
                          Move up
                        </button>
                        <button type="button" onClick={() => moveField(index, 1)} className="text-[12px] font-bold text-burgundy-faint">
                          Move down
                        </button>
                        <button
                          type="button"
                          onClick={() => setFields((list) => list.filter((f) => f.id !== field.id))}
                          className="text-[12px] font-bold text-danger"
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="min-h-11 px-5 rounded-lg bg-pink text-cream-bright font-bold text-[13px] disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save form'}
              </button>
            </div>
          </section>

          <aside className="bg-cream-bright border border-burgundy/[0.12] rounded-xl p-4 h-fit">
            <h2 className="font-bagel text-[22px] text-burgundy mb-2">Publish link</h2>
            <p className="text-[13px] text-burgundy-faint mb-3">
              Set status to Live, save, then copy this link.
            </p>
            <div className="rounded-lg bg-cream p-3 text-[13px] text-burgundy break-all mb-3">
              {publicLink || 'Add a slug first'}
            </div>
            {publicLink && (
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(publicLink)}
                className="min-h-10 px-3 rounded-lg bg-burgundy text-cream-bright text-[12px] font-bold"
              >
                Copy link
              </button>
            )}
            <div className="mt-5 text-[12px] text-burgundy-faint">
              Custom form responses land in the same inbox and can be filtered later by form.
            </div>
          </aside>
        </form>
      </main>
    </AdminPageShell>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-bold uppercase text-burgundy-faint">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        className="min-h-10 rounded-lg border border-burgundy/[0.18] bg-cream px-3 text-[14px] focus:outline-none focus:border-pink disabled:opacity-60"
      />
    </label>
  );
}

function Select({
  label,
  value,
  onChange,
  disabled,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-bold uppercase text-burgundy-faint">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="min-h-10 rounded-lg border border-burgundy/[0.18] bg-cream px-3 text-[14px] focus:outline-none focus:border-pink disabled:opacity-60"
      >
        {children}
      </select>
    </label>
  );
}

function Badge({ children }: { children: ReactNode }) {
  return (
    <span className="rounded bg-burgundy/[0.08] px-2 py-0.5 text-[10px] font-bold uppercase text-burgundy-soft">
      {children}
    </span>
  );
}
